import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { BiExport } from "react-icons/bi";
import Fuse from "fuse.js";

import listAllUsers from "features/panelManagement/accessManagement/actions/access/access.listAllUsers";
import { useDispatch, useSelector } from "services/redux/rootReducer";
import getAllLogs from "../actions/get.getAllLogs";
import AutoCompleteSelect from "features/shared/components/form/components/autoCompleteSelect";
import { downloadJSONasExcel } from "features/shared/functions/excelOperations";

const LoggingPage = () => {
    const dispatch = useDispatch();
    const logs = useSelector((state) => state.logs);
    const users = useSelector((state) => state.access.users);

    const [filter, setFilter] = useState<string>("");

    useEffect(() => {
        let unsubscribers: Array<() => void> = [];

        dispatch(listAllUsers());
        unsubscribers.push(dispatch(getAllLogs()));

        return () => {
            unsubscribers.forEach((unsubscriberFn) => unsubscriberFn());
        };
    }, [dispatch]);

    const handleExportLogs = () => {
        const exportedLogs = filteredLogs.map((log) => ({
            "Created At": log.creationTimestamp,
            Name: log.displayName,
            UID: log.userInfo?.uid,
            Description: log.eventInfo?.description,
        }));
        downloadJSONasExcel(exportedLogs, `Neotech Admin Logs`);
    };

    const filteredLogs = useMemo(() => {
        const mappedLogs = logs
            .sort((a, b) => {
                return (b.creationTimestamp || 0) - (a.creationTimestamp || 0);
            })
            .map((log) => {
                return {
                    ...log,
                    displayName:
                        users?.find((user) => user.uid === log.userInfo?.uid)
                            ?.displayName || "",
                    creationTimestamp: new Date(
                        log.creationTimestamp || 0
                    ).toLocaleString(),
                };
            });

        let filteredLogs = mappedLogs;

        if (filter.trim()) {
            let key: string = "";

            switch (filter.split(":")[0]) {
                case "time":
                    key = "creationTimestamp";
                    break;
                case "event":
                    key = "eventInfo.description";
                    break;
                case "user":
                    key = "displayName";
            }

            const fuse = new Fuse(mappedLogs, {
                threshold: 0.2,
                keys: [
                    key || "creationTimestamp",
                    "eventInfo.description",
                    "displayName",
                ],
            });
            filteredLogs = fuse
                .search(filter.split(":").slice(1).join() || filter)
                .map((result) => result.item);
        }
        return filteredLogs;
    }, [filter, logs, users]);

    const displayedLogs = useMemo(() => {
        const displayedElements = filteredLogs.map((log) => {
            return (
                <LogEntryContainer key={log.creationTimestamp}>
                    <LogEntry>
                        <span>{`[${log.creationTimestamp}] [${log.displayName}]`}</span>
                        <span>{log.eventInfo?.description}</span>
                    </LogEntry>
                    <hr />
                </LogEntryContainer>
            );
        });

        return displayedElements;
    }, [filteredLogs]);

    return (
        <Container>
            <LogsContainer>
                <HeaderTab>
                    <span>ACTIVITY LOGS</span>
                    <ButtonContainer>
                        <Searchbar>
                            <AutoCompleteSelect
                                initialSuggestions={[
                                    "event:",
                                    "user:",
                                    "time:",
                                ]}
                                queriedStrings={["event:", "user:", "time:"]}
                                name="-"
                                value={filter}
                                onChangeHandler={(_name, query) => {
                                    setFilter(query);
                                }}
                            />
                        </Searchbar>
                        <StyledButton onClick={() => handleExportLogs()}>
                            <BiExport />
                        </StyledButton>
                    </ButtonContainer>
                </HeaderTab>
                <Logs>{displayedLogs}</Logs>
            </LogsContainer>
        </Container>
    );
};

const Container = styled.div`
    position: relative;
    display: flex;
    background: ${(props) => props.theme.colors.neutral[50]};

    & > div {
        margin: 2.5%;
        border-radius: 0.2rem;

        background: white;
        color: ${(props) => props.theme.colors.neutral[800]};
        box-shadow: 1px 0px 10px 1px rgba(231, 231, 231, 0.5);
        flex: 1;
    }

    // For mobile
    @media (max-width: 768px) {
        flex-flow: column;

        & > div {
            margin: 0;
        }
    }
`;

const LogsContainer = styled.div`
    display: flex;
    flex-flow: column;
    box-shadow: 1px 0px 10px 1px rgba(0, 26, 255, 0.1);

    width: 80%;

    & > * {
        width: 100%;

        &:first-child {
            flex: 0 0 4rem;
            border-radius: 0.2rem 0.2rem 0 0;
        }
        &:last-child {
            flex: 1;
            overflow-y: auto;
            height: 0px;
            border-radius: 0 0 0.2rem 0.2rem;
        }
    }

    @media (max-width: 768px) {
        min-height: 8rem;
        width: 100%;

        & > *:first-child > *:nth-child(2) {
            & > *:first-child {
                display: none;
            }
        }
    }
`;

const HeaderTab = styled.div`
    background: ${(props) => props.theme.colors.neutral[135]};
    color: #929cb9;

    display: flex;
    align-items: center;
    justify-content: space-between;

    user-select: none;

    & > * {
        font-size: 1rem;
        font-weight: ${(props) => props.theme.fontWeight.bolder};
        margin-left: 2rem;

        &:last-child {
            margin-left: 0.5rem;
        }
    }
`;

const Logs = styled.div`
    color: ${(props) => props.theme.colors.primary[100]};
    padding: 2rem;
`;

const LogEntryContainer = styled.div`
    display: flex;
    flex-flow: column;

    & > hr {
        color: white;
        border-top: 1px solid ${(props) => props.theme.colors.neutral[125]};
        margin: 0.25rem 0;
        width: 95%;
        align-self: center;
    }
`;

const LogEntry = styled.div`
    display: flex;
    justify-content: space-between;

    font-size: 0.9rem;

    & > *:last-child {
        max-width: 50%;
    }
`;

const ButtonContainer = styled.div`
    display: flex;

    & > * {
        margin-right: 1rem;

        &:last-child {
            margin-right: 2rem;
        }
    }
`;

const StyledButton = styled.div<{ isActive?: boolean }>`
    width: 2rem;
    height: 2rem;

    display: flex;
    justify-content: center;
    align-items: center;

    background: ${(props) =>
        props.isActive
            ? props.theme.colors.success[100]
            : props.theme.colors.neutral[25]};
    color: ${(props) =>
        props.isActive ? props.theme.colors.success[500] : "inherit"};
    box-shadow: 1px 0px 10px 1px rgba(205, 222, 255, 0.5);

    border-radius: 0.2rem;

    font-size: 1.5rem;

    cursor: pointer;

    &:hover {
        background: white;
    }
`;

const Searchbar = styled.div`
    background: white;
    border-radius: 0.2rem;
    width: 14rem;
    display: flex;

    & > * {
        flex: 1;
        font-weight: ${(props) => props.theme.fontWeight.normal};

        input {
            padding: 0 0.5rem;
            color: #a7b1c5;
            border: solid 1px ${(props) => props.theme.colors.neutral[200]} !important;

            &::placeholder {
                color: ${(props) => props.theme.colors.neutral[300]};
            }
        }
    }
`;

export default LoggingPage;
